import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";


export type HealPicks = {
    heal: boolean;
    armour: boolean;
    revive: boolean;
};

const emptyPicks: HealPicks = { heal: false, armour: false, revive: false };


type HealOptionProps = {
    label: string;
    checked: boolean;
    blockedReason?: React.ReactNode;
    onCheckedChange: (_checked: boolean) => void;
};

function HealOption({ label, checked, blockedReason, onCheckedChange }: HealOptionProps) {
    const isBlocked = !!blockedReason;
    //a plain label sizes differently than a wrapped one, so all three keep the
    //same wrapper and only the blocked ones become a tooltip trigger
    const tile = (
        <span className={cn('flex flex-1 min-w-0', isBlocked && 'cursor-not-allowed')}>
            <label className={cn(
                'flex flex-1 min-w-0 items-center justify-center gap-2 whitespace-nowrap',
                'rounded-bar border border-edge bg-tile px-2.5 py-2 text-sm select-none',
                'text-dim transition-colors',
                isBlocked
                    ? 'opacity-40 pointer-events-none'
                    : 'cursor-pointer hover:border-accent/45 hover:bg-accent/10 hover:text-fg',
                checked && 'border-accent/45 bg-accent/10 text-fg hover:border-accent/70 hover:bg-accent/20',
            )}>
                <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onCheckedChange(value === true)}
                    disabled={isBlocked}
                />
                {label}
            </label>
        </span>
    );
    if (!isBlocked) return tile;

    return (
        <Tooltip>
            {/* a disabled control emits no pointer events, so the hover lives on the wrapper */}
            <TooltipTrigger asChild tabIndex={0}>
                {tile}
            </TooltipTrigger>
            <TooltipContent className="text-destructive-inline text-center">
                {blockedReason}
            </TooltipContent>
        </Tooltip>
    );
}


type PlayerHealthDialogProps = {
    isOpen: boolean;
    playerName: string;
    canHeal: boolean;
    canRevive: boolean;
    isTargetDead: boolean;
    onOpenChange: (_isOpen: boolean) => void;
    onSubmit: (_picks: HealPicks) => void;
};

export default function PlayerHealthDialog({
    isOpen,
    playerName,
    canHeal,
    canRevive,
    isTargetDead,
    onOpenChange,
    onSubmit,
}: PlayerHealthDialogProps) {
    //nothing can be restored on a dead player without reviving them first
    const isReviveForced = isTargetDead && canRevive;
    const [picks, setPicks] = useState<HealPicks>(emptyPicks);

    //start clean on every open, an old selection would be applied by accident
    useEffect(() => {
        if (isOpen) setPicks({ ...emptyPicks, revive: isReviveForced });
    }, [isOpen, isReviveForced]);

    const healBlocked = canHeal ? undefined : <p>
        You need the "Heal" permission<br />
        to heal players.
    </p>;
    let reviveBlocked;
    if (!canRevive) {
        reviveBlocked = <p>
            You need the "Revive" permission<br />
            to revive players.
        </p>;
    } else if (isReviveForced) {
        reviveBlocked = <p>
            Reviving is required<br />
            while the player is dead.
        </p>;
    }
    const hasPick = picks.heal || picks.armour || picks.revive;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!hasPick) return;
        onSubmit(picks);
        onOpenChange(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                onOpenAutoFocus={(event) => {
                    //a blocked option stays focusable so its reason can be read, but
                    //landing on it while opening would pop that tooltip on its own
                    const firstFree = (event.currentTarget as HTMLElement)
                        ?.querySelector<HTMLElement>('button[role="checkbox"]:not([disabled])');
                    if (!firstFree) return;
                    event.preventDefault();
                    firstFree.focus();
                }}
            >
                <form onSubmit={handleSubmit} className="grid gap-4">
                    <DialogHeader>
                        <DialogTitle>Health of {playerName}</DialogTitle>
                        <DialogDescription>
                            Pick what to restore for this player.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-stretch gap-2">
                        <HealOption
                            label="Heal"
                            checked={picks.heal}
                            blockedReason={healBlocked}
                            onCheckedChange={(checked) => setPicks((prev) => ({ ...prev, heal: checked }))}
                        />
                        <HealOption
                            label="Armour"
                            checked={picks.armour}
                            blockedReason={healBlocked}
                            onCheckedChange={(checked) => setPicks((prev) => ({ ...prev, armour: checked }))}
                        />
                        <HealOption
                            label="Revive"
                            checked={picks.revive}
                            blockedReason={reviveBlocked}
                            onCheckedChange={(checked) => setPicks((prev) => ({ ...prev, revive: checked }))}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={!hasPick}>
                            Apply
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
